// The arithmetic of the Tender's three marks (R725, `ui-map-interface.md`
// §6.1 and §6.3.2).
//
// The heal beam breathes, the regen wave grows and fades, and a shield plus
// rises when a ring passes a hull. All three are plain functions of a clock,
// so they live here and not in a layer: the fleet layer holds the state and
// packs the instances, and this module answers what the marks look like at
// time `t`.
//
// Every function that returns more than one number writes into a caller-owned
// `out` object, because a layer calls them once a ring a frame and §10 forbids
// a per-frame allocation.

import type { TenderInk } from '../gen/presentation';
import { TAU } from './math';

/** The alpha and width multipliers of one breath. */
export interface Breath {
  alpha: number;
  width: number;
}

/** The place, the ink and the stroke of one ring of the wave. */
export interface WaveRing {
  r: number;
  alpha: number;
  width: number;
}

/**
 * Which colour a mark wears.
 *
 * `faction` takes the ink of the hull the mark belongs to, and `verdict`
 * takes the colour the mark's own entry carries. `factionColor` is handed in
 * rather than imported, so this leaf stays a function of its arguments and a
 * test can pin both branches without a palette.
 */
export function inkOf(
  ink: TenderInk, faction: number, verdict: number,
  factionColor: (f: number) => number,
): number {
  return ink === 'faction' ? factionColor(faction) : verdict;
}

/**
 * The breath of the heal beam at time `t`, in seconds.
 *
 * `p = 0.5 + 0.5 sin(2 pi hz t)` runs 0 to 1. The beam multiplies the alpha
 * and the width of every stroke of `CONTROL_BEAM_TIERS` by the two results,
 * so a base of 0 would put the beam out at the bottom of the breath.
 */
export function breath(
  t: number, hz: number, aBase: number, aSwing: number,
  wBase: number, wSwing: number, out: Breath,
): Breath {
  const p = 0.5 + 0.5 * Math.sin(TAU * hz * t);
  out.alpha = aBase + aSwing * p;
  out.width = wBase + wSwing * p;
  return out;
}

/**
 * How far ring `k` of `count` has run, 0 at its birth and 1 at the edge.
 *
 * The rings share one period and stand `1 / count` apart in it, so a wave of
 * two puts one ring at the half way mark while the other is born. `phase`
 * offsets the whole wave, so two Tenders side by side do not breathe
 * together.
 */
export function waveU(
  t: number, k: number, count: number, periodSec: number, phase: number,
): number {
  const u = t / periodSec + phase + k / count;
  return u - Math.floor(u);
}

/**
 * One ring of the wave at progress `u`.
 *
 * It grows from `r0` to `r1`, it thins from `w0` to `w1`, and its alpha runs
 * from `alpha0` to nothing. The alpha reaches zero exactly at the edge, so
 * the aura carries no rim.
 */
export function waveAt(
  u: number, r0: number, r1: number, alpha0: number,
  w0: number, w1: number, out: WaveRing,
): WaveRing {
  out.r = r0 + (r1 - r0) * u;
  out.alpha = alpha0 * (1 - u);
  out.width = w0 + (w1 - w0) * u;
  return out;
}

/**
 * Whether a ring crossed the hull at distance `d` between two frames.
 *
 * The test is strict on the far side, so a hull the ring is born on top of
 * takes no plus twice. A ring that was reborn — `rNow` under `rPrev` — never
 * counts as a crossing, because it jumped back to the anchor ring rather
 * than travelling there.
 */
export function ringPassed(rPrev: number, rNow: number, d: number): boolean {
  // A reborn ring fails the test by itself: rNow < rPrev < d.
  return rPrev < d && rNow >= d;
}

/**
 * The phase of one Tender's wave, from its fleet id.
 *
 * 97 is the prime the sov beam's pip offset takes (§6.5), for the same
 * reason: a row of Tenders must not pulse in step.
 */
export function wavePhase(id: number): number {
  return (id % 97) / 97;
}
